import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth/auth.service';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { HelperService } from '../../../services/helper/helper.service';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ResetPasswordService } from '../../../services/reset-password/reset-password.service';
import { CustomModalComponent } from '../../misc/custom-modal/custom-modal.component';
import { LoadingComponent } from '../../loading/loading.component';
import { NgIf } from '@angular/common';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { AwOtpInputComponent } from '../../misc/aw-otp-input/aw-otp-input.component';
import { AuthPageService } from '@services/auth/auth-page.service';
import { fade } from '../../../animations/fade';
import { Auth, MultiFactorError, RecaptchaVerifier, User } from '@angular/fire/auth';
import { ToastService } from '@services/helper/toast.service';

@Component({
    selector: 'app-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    animations: [fade],
    imports: [NgIf, FormsModule, ReactiveFormsModule, LoadingComponent, CustomModalComponent, TranslateModule, AwOtpInputComponent]
})
export class SignInComponent implements OnInit, OnDestroy {
    formSubmitted = false;
    signInForm: UntypedFormGroup;
    resetPWLoading: boolean;
    mfaError: MultiFactorError;
    otpCode: string;
    phone: string;

    @ViewChild('recaptchaContainer', { static: true }) recaptchaContainer: ElementRef;
    recaptchaVerifier: RecaptchaVerifier;
    verificationId: string;

    private auth: Auth = inject(Auth);

    constructor(
        protected modalService: NgbModal,
        private router: Router,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private formBuilder: UntypedFormBuilder,
        private translate: TranslateService,
        private helperService: HelperService,
        protected authPageService: AuthPageService,
        private title: Title,
        private resetPasswordService: ResetPasswordService,
        private toastService: ToastService
    ) {}

    async ngOnInit() {
        this.title.setTitle('Sign in');
        this.checkForToast();
        this.checkForIncorrectUser();
        const prefillMail: string = this.checkForPrefillMail();
        const user: DashboardOwnerUser = this.helperService.getDashboardOwnerUser(true);
        if (user) this.router.navigate([`/home`]);
        this.signInForm = this.formBuilder.group({
            email: new UntypedFormControl(prefillMail ? prefillMail : null, [Validators.required]),
            password: new UntypedFormControl(null, [Validators.required])
        });
        // Initialize the reCAPTCHA verifier
        this.recaptchaContainer.nativeElement.innerHTML = ''; // Ensure the container is empty
        this.recaptchaVerifier = new RecaptchaVerifier(this.auth, this.recaptchaContainer.nativeElement, { size: 'invisible' });
        // Render the reCAPTCHA once
        await this.recaptchaVerifier.render();
    }

    ngOnDestroy() {
        this.authPageService.resetMfaToggles();
    }

    checkForIncorrectUser() {
        if (!this.helperService.compareUserToLocalStorage('dashboardOwnerUser')) {
            this.localStorageService.removeItem('dashboardOwnerUser');
        }
    }

    checkForPrefillMail(): string {
        const prefillMail: string = this.localStorageService.getItem('prefillMail');
        if (prefillMail) {
            this.localStorageService.removeItem('prefillMail');
        }
        return prefillMail;
    }

    checkForToast(): void {
        if (this.localStorageService.getItem('passwordSet') === 'true') {
            this.localStorageService.removeItem('prefillMail');
            this.toastService.info(this.translate.instant('customers-set-password.please_sign_in'), this.translate.instant('customers-set-password.password_set'), { timeOut: 10000 });
        } else if (sessionStorage.getItem('email_update_flag') === 'true') {
            sessionStorage.removeItem('email_update_flag');
            this.toastService.success(this.translate.instant('account.email_updated'));
        }
    }

    get password() {
        return this.signInForm.get('password');
    }

    get email() {
        return this.signInForm.get('email');
    }

    async signIn() {
        this.formSubmitted = true;
        if (this.signInForm.valid) {
            this.authPageService.loading.set(true);
            if (this.authService.isSignedIn()) {
                await this.authService.signOut(true); // Sign out before signing in to avoid two active sessions at the same time
            }
            this.toastService.remove();
            const emailLowerCased = this.signInForm.value['email'].replace(/\s/g, '').toLowerCase();
            this.authService.signIn(emailLowerCased, this.signInForm.value['password']).catch(err => {
                if (err?.code === 'auth/multi-factor-auth-required') {
                    this.authPageService.loading.set(false);
                    this.handleMfaError(err);
                } else {
                    this.toastService.warning(this.translate.instant('sign-in.err_proccess_log_in'));
                    this.authPageService.loading.set(false);
                }
            });
        }
    }

    handleMfaError(err) {
        this.mfaError = err;
        // Check if Totp MFA is enabled on the account, if not, show phone MFA
        this.authPageService.checkMfaStatusFromError(this.auth, this.mfaError);
        if (this.authPageService.hasTotpMfaEnabled()) {
            this.authPageService.isMfaTotpSignIn.set(true);
        } else if (this.authPageService.hasPhoneMfaEnabled()) {
            this.sendPhoneMfa();
        }
    }

    async catchOtpEvent(otp: string, sms?: boolean) {
        this.otpCode = otp;
        if (sms) {
            await this.handlePhoneMfa();
        } else {
            await this.handleTotpMfa();
        }
    }

    async sendPhoneMfa() {
        if (!this.authPageService.hasPhoneMfaEnabled()) {
            this.toastService.mfaToast(this.translate.instant('account.mfa.ops'), this.translate.instant('account.mfa.no_sms_yet_title'), this.translate.instant('account.mfa.no_sms_yet_desc'), 'warning');
            return;
        }
        this.authPageService.loading.set(true);
        this.verificationId = await this.authService
            .sendPhoneVerificationCode(this.mfaError, this.recaptchaVerifier)
            .then(val => {
                return val;
            })
            .catch(error => {
                console.error('Error sending phone verification code:', error);
                this.authPageService.loading.set(false);
                if (error.code === 'auth/invalid-phone-number') {
                    this.toastService.warning(this.translate.instant('account.mfa.error.invalid_phone_number'));
                } else if (error.code === 'auth/quota-exceeded') {
                    this.toastService.error(this.translate.instant('account.mfa.quota_exceeded'));
                } else if (error.code === 'auth/too-many-requests') {
                    this.toastService.error(this.translate.instant('account.mfa.error.too_many_requests'));
                } else {
                    this.toastService.error(this.translate.instant('account.mfa.unidentified_error'));
                }

                return '';
            });

        this.authPageService.hasSentSms.set(true);
        this.authPageService.isMfaPhoneSignIn.set(true);
        this.authPageService.isMfaTotpSignIn.set(false);
        this.authPageService.loading.set(false);
    }

    async handlePhoneMfa() {
        console.log('Handling phone MFA with code:', this.otpCode);
        this.authPageService.loading.set(true);
        await this.authService
            .verifyPhoneCodeAndSignIn(this.mfaError, this.verificationId, this.otpCode)
            .then(() => {
                console.log('Phone MFA verification successful');
                const user = this.auth.currentUser as User;
                this.authService.afterSigninProcess(user).finally(() => {
                    this.authPageService.loading.set(false);
                });
            })
            .catch(error => {
                console.log('Error during phone MFA:', error);
                if (error.code === 'auth/invalid-verification-code') {
                    this.toastService.warning(this.translate.instant('account.mfa.invalid_mfa'));
                } else if (error.code === 'auth/quota-exceeded') {
                    this.toastService.error(this.translate.instant('account.mfa.quota_exceeded'));
                } else if (error.code === 'auth/too-many-requests') {
                    this.toastService.error(this.translate.instant('account.mfa.error.too_many_requests'));
                } else {
                    this.toastService.error(this.translate.instant('account.mfa.unidentified_error'));
                }
                this.authPageService.loading.set(false);
            });
    }

    async handleTotpMfa() {
        console.log('Handling TOTP MFA with code:', this.otpCode);
        this.authPageService.loading.set(true);
        await this.authService
            .handleTotpMfa(this.mfaError, this.otpCode)
            .then(() => {
                const user = this.auth.currentUser as User;
                this.authService.afterSigninProcess(user).finally(() => {
                    this.authPageService.loading.set(false);
                });
            })
            .catch(error => {
                if (error.code === 'auth/invalid-verification-code') {
                    this.toastService.warning(this.translate.instant('account.mfa.invalid_mfa'));
                } else if (error.code === 'auth/quota-exceeded') {
                    this.toastService.error(this.translate.instant('account.mfa.quota_exceeded'));
                } else if (error.code === 'auth/too-many-requests') {
                    this.toastService.error(this.translate.instant('account.mfa.error.too_many_requests'));
                } else {
                    this.toastService.error(this.translate.instant('account.mfa.unidentified_error'));
                }
                this.authPageService.loading.set(false);
            });
    }

    async resetPassword() {
        this.resetPWLoading = true;
        const emailElement: HTMLInputElement = document.getElementById('forgot-password') as HTMLInputElement;
        if (!emailElement.value) {
            this.toastService.warning(this.translate.instant('sign-in.type_in_email'));
            document.getElementById('forgot-password').focus();
            this.resetPWLoading = false;
        } else if (!emailElement.checkValidity()) {
            this.toastService.warning(this.translate.instant('sign-in.invalid_email_typed'));
            document.getElementById('forgot-password').focus();
            this.resetPWLoading = false;
        } else {
            await this.resetPasswordService.requestResetPasswordEmail(emailElement.value).catch(() => {});
            this.toastService.success(this.translate.instant('sign-in.reset_link_msg'));
            this.resetPWLoading = false;
            this.modalService.dismissAll();
        }
    }

    openModal(modal: any) {
        const modalOptions: NgbModalOptions = {
            ariaLabelledBy: 'modal-basic-title'
        };
        this.modalService
            .open(modal, modalOptions)
            .result.then((event: any) => {})
            .catch((event: any) => {});
    }
}
